import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import KuBadge from '../../components/common/KuBadge'
import Stars from '../../components/common/Stars'
import { useAuth } from '../../providers/AuthProvider'
import { useStandards } from '../../providers/StandardsProvider'
import { useKUs } from '../../hooks/useKUs'
import { KnowledgeUnit, KU_TYPES, PASS_META, ROLE_CAPS } from '../../types'

export default function KUDetailPage() {
  const { stdId, kuId } = useParams<{ stdId: string; kuId: string }>()
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const caps = ROLE_CAPS[profile?.role || 'teacher']
  const { findStandard } = useStandards()
  const { fetchKUs, updateKU, upsertVote } = useKUs()

  const [ku, setKU] = useState<KnowledgeUnit | null>(null)
  const [allKUs, setAllKUs] = useState<KnowledgeUnit[]>([])
  const [tab, setTab] = useState(0)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [myScore, setMyScore] = useState(0)
  const [myComment, setMyComment] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const stdMeta = stdId ? findStandard(stdId) : null

  const loadData = useCallback(async () => {
    if (!stdId) return
    const kus = await fetchKUs(stdId)
    setAllKUs(kus)
    const found = kus.find((k) => k.id === kuId)
    if (found) {
      setKU(found)
      setEditName(found.name)
      setEditDesc(found.description)
      const myVote = found.votes?.find((v) => v.user_id === user?.id)
      setMyScore(myVote?.score || 0)
      setMyComment(myVote?.comment || '')
    }
  }, [stdId, kuId, fetchKUs, user?.id])

  useEffect(() => {
    loadData()
  }, [loadData])

  const passKUs = allKUs.filter((k) => ku && k.pass_id === ku.pass_id)
  const passIdx = passKUs.findIndex((k) => k.id === kuId)

  const navTo = (targetId: string) => navigate(`/standard/${stdId}/ku/${targetId}`)

  const saveEdit = async () => {
    if (!ku) return
    setSaving(true)
    const updated = await updateKU(ku.id, { name: editName, description: editDesc })
    if (updated) {
      setKU(updated)
      setEditing(false)
    }
    setSaving(false)
  }

  const submitVote = async () => {
    if (!ku || !user || !myScore) return
    setSaving(true)
    await upsertVote(ku.id, user.id, myScore, myComment)
    await loadData()
    setSaving(false)
  }

  const setDecision = async (decision: 'accepted' | 'inReview' | 'rejected') => {
    if (!ku) return
    setSaving(true)
    const statusMap = { accepted: 'accepted', inReview: 'inReview', rejected: 'rejected' } as const
    const updated = await updateKU(ku.id, { status: statusMap[decision], admin_decision: decision })
    if (updated) setKU(updated)
    setSaving(false)
  }

  const setType = async (type: string) => {
    if (!ku) return
    const updated = await updateKU(ku.id, { type: type as any })
    if (updated) setKU(updated)
  }

  if (!ku) return null

  const pass = PASS_META.find((p) => p.id === ku.pass_id)!
  const avgScore = ku.votes?.length
    ? Math.round((ku.votes.reduce((s, v) => s + v.score, 0) / ku.votes.length) * 10) / 10
    : 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      {/* Breadcrumb + prev/next nav */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 1.5,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton size="small" onClick={() => navigate(`/standard/${stdId}`)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 12 }}>
            Standard
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            disabled={passIdx <= 0}
            onClick={() => passIdx > 0 && navTo(passKUs[passIdx - 1].id)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
            {passIdx + 1} / {passKUs.length}
          </Typography>
          <IconButton
            size="small"
            disabled={passIdx >= passKUs.length - 1}
            onClick={() => passIdx < passKUs.length - 1 && navTo(passKUs[passIdx + 1].id)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* KU identity bar */}
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            {editing ? (
              <TextField
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 0.5 }}
                inputProps={{ style: { fontWeight: 800, fontSize: 15 } }}
              />
            ) : (
              <Typography variant="h6" sx={{ fontSize: 15, fontWeight: 800, color: 'primary.main', lineHeight: 1.3 }}>
                {ku.name}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>
              {pass.icon} {pass.label} · {stdId}
            </Typography>
          </Box>
          {caps.canEditKU && !editing && (
            <IconButton size="small" onClick={() => setEditing(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {editing && (
            <Button size="small" startIcon={<SaveIcon />} onClick={saveEdit} disabled={saving} variant="contained">
              Save
            </Button>
          )}
        </Box>
      </Box>

      {/* Tab bar */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40 }}>
          <Tab label="KU" sx={{ fontWeight: 700, fontSize: 12, minHeight: 40 }} />
          <Tab label={`§ Source (${pass.label})`} sx={{ fontWeight: 700, fontSize: 12, minHeight: 40 }} />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* KU tab */}
      {tab === 0 && (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {/* Description */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontSize: 10,
                  display: 'block',
                  mb: 1,
                }}
              >
                Description
              </Typography>
              {editing ? (
                <TextField
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                />
              ) : (
                <Typography variant="body2" sx={{ lineHeight: 1.7, fontSize: 13 }}>
                  {ku.description}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Type */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontSize: 10,
                  display: 'block',
                  mb: 1,
                }}
              >
                Knowledge Type
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                    AI Proposed
                  </Typography>
                  <Box>
                    <KuBadge type={ku.proposed_type} />
                  </Box>
                </Box>
                {caps.canDecide && (
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel sx={{ fontSize: 12 }}>Confirmed Type</InputLabel>
                    <Select
                      value={ku.type || ''}
                      label="Confirmed Type"
                      onChange={(e) => setType(e.target.value)}
                      sx={{ fontSize: 12 }}
                    >
                      <MenuItem value="" sx={{ fontSize: 12 }}>
                        <em>Not set</em>
                      </MenuItem>
                      {KU_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value} sx={{ fontSize: 12 }}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: t.color,
                              mr: 1,
                            }}
                          />
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Vote section */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    fontSize: 10,
                  }}
                >
                  Your Vote
                </Typography>
                {ku.votes && ku.votes.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                    Avg: {avgScore} ({ku.votes.length} vote{ku.votes.length !== 1 ? 's' : ''})
                  </Typography>
                )}
              </Box>
              <Stars value={myScore} onRate={setMyScore} size={24} interactive />
              <TextField
                placeholder="Comment (optional)"
                value={myComment}
                onChange={(e) => setMyComment(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
                sx={{ mt: 1.5, mb: 1.5 }}
              />
              <Button variant="contained" size="small" onClick={submitVote} disabled={!myScore || saving} fullWidth>
                Submit Vote
              </Button>
            </CardContent>
          </Card>

          {/* Admin decision */}
          {caps.canDecide && (
            <Card variant="outlined" sx={{ mb: 2, borderColor: '#f59e0b40' }}>
              <CardContent>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    fontSize: 10,
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  Admin Decision
                </Typography>
                <ToggleButtonGroup value={ku.admin_decision} exclusive size="small" sx={{ mb: 1.5 }}>
                  <ToggleButton
                    value="accepted"
                    onClick={() => setDecision('accepted')}
                    sx={{ color: '#16a34a', '&.Mui-selected': { bgcolor: '#dcfce7', color: '#16a34a' } }}
                  >
                    Accept
                  </ToggleButton>
                  <ToggleButton
                    value="inReview"
                    onClick={() => setDecision('inReview')}
                    sx={{ color: '#d97706', '&.Mui-selected': { bgcolor: '#fef3c7', color: '#d97706' } }}
                  >
                    In Review
                  </ToggleButton>
                  <ToggleButton
                    value="rejected"
                    onClick={() => setDecision('rejected')}
                    sx={{ color: '#dc2626', '&.Mui-selected': { bgcolor: '#fee2e2', color: '#dc2626' } }}
                  >
                    Reject
                  </ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  placeholder="Admin note"
                  value={ku.admin_note || ''}
                  onChange={(e) => updateKU(ku.id, { admin_note: e.target.value })}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                />
              </CardContent>
            </Card>
          )}

          {/* Provenance */}
          <Card variant="outlined">
            <CardContent>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontSize: 10,
                  display: 'block',
                  mb: 1,
                }}
              >
                Provenance
              </Typography>
              {ku.run && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 80, fontSize: 11 }}>
                      Provider
                    </Typography>
                    <Chip label={ku.run.provider} size="small" sx={{ height: 18, fontSize: 10 }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 80, fontSize: 11 }}>
                      Model
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 11, fontFamily: 'monospace' }}>
                      {ku.run.model}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 80, fontSize: 11 }}>
                      Prompt
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 11 }}>
                      {ku.run.prompt_label}
                    </Typography>
                  </Box>
                  {ku.run.latency_ms && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 80, fontSize: 11 }}>
                        Latency
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: 11 }}>
                        {ku.run.latency_ms}ms
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Source tab */}
      {tab === 1 && (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <Card variant="outlined" sx={{ mb: 2, borderColor: pass.color + '40' }}>
            <CardContent sx={{ bgcolor: pass.bg }}>
              <Typography
                variant="caption"
                sx={{
                  color: pass.color,
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontSize: 10,
                }}
              >
                {pass.icon} {pass.label} Source
              </Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontSize: 10,
                  display: 'block',
                  mb: 1,
                }}
              >
                Standard Text
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7, fontSize: 13 }}>
                {stdMeta?.std.text}
              </Typography>
            </CardContent>
          </Card>

          {ku.run?.source_doc && (
            <Card variant="outlined">
              <CardContent>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    fontSize: 10,
                    display: 'block',
                    mb: 1,
                  }}
                >
                  {ku.pass_id === 'pass2' ? 'Lesson Plan' : 'Domain Knowledge'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ lineHeight: 1.7, fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                >
                  {ku.run.source_doc}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  )
}
